// src/screens/dashboard/Dashboard.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  DollarSign, Target, BrainCircuit, Users, TrendingUp, Sparkles, ArrowRight,
  Info, Activity, UserCheck, MousePointer2, Mail, Link as LinkIcon,
  ShoppingCart, CheckCircle2
} from 'lucide-react';
import '../dashboard/Dashboard.css';


const Dashboard = () => {
  const [chartTab, setChartTab] = useState('Revenue');

  return (
    <>
      <Helmet>
        <title>Dashboard - Revexia AI Revenue Brain</title>
        <meta name="description" content="Revexia Dashboard - Track DM revenue, conversions, and AI predictions" />
      </Helmet>

      <div className="container-fluid px-4">

        {/* ── KPI Cards ── */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="kpi-stat-card kpi-blue">
              <div className="kpi-header-row">
                <div className="kpi-icon-box"><DollarSign size={20} strokeWidth={2} /></div>
                <div className="kpi-trend-badge trend-up"><TrendingUp size={12} /><span>14.2%</span></div>
              </div>
              <div className="kpi-label-text">DM REVENUE (30D)</div>
              <div className="kpi-value-text">$142,508</div>
              <div className="kpi-progress-track">
                <div className="kpi-progress-fill progress-blue" style={{ width: '75%' }}></div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <div className="kpi-stat-card kpi-purple">
              <div className="kpi-header-row">
                <div className="kpi-icon-box"><Target size={20} strokeWidth={2} /></div>
                <div className="kpi-trend-badge trend-up"><TrendingUp size={12} /><span>2.1%</span></div>
              </div>
              <div className="kpi-label-text">% OFF</div>
              <div className="kpi-value-text">4.25%</div>
              <div className="kpi-progress-track">
                <div className="kpi-progress-fill progress-purple" style={{ width: '60%' }}></div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <div className="kpi-stat-card kpi-orange">
              <div className="kpi-header-row">
                <div className="kpi-icon-box"><Activity size={20} strokeWidth={2} /></div>
                <div className="kpi-trend-badge trend-up"><TrendingUp size={12} /><span>8.4%</span></div>
              </div>
              <div className="kpi-label-text">PURCHASE AFTER FOLLOW-UPS</div>
              <div className="kpi-value-text">$145.20</div>
              <div className="kpi-progress-track">
                <div className="kpi-progress-fill progress-orange" style={{ width: '82%' }}></div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <div className="kpi-stat-card kpi-cyan">
              <div className="kpi-header-row">
                <div className="kpi-icon-box"><UserCheck size={20} strokeWidth={2} /></div>
                <div className="kpi-trend-badge trend-up"><TrendingUp size={12} /><span>1.2%</span></div>
              </div>
              <div className="kpi-label-text">ABANDONED OFFER FOLLOW-UPS</div>
              <div className="kpi-value-text">24.8%</div>
              <div className="kpi-progress-track">
                <div className="kpi-progress-fill progress-cyan" style={{ width: '68%' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Revenue Chart ── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="chart-card-container">
              <div className="chart-card-header">
                <div className="chart-header-info">
                  <h2 className="chart-card-title">
                    Revenue Performance
                    <span className="realtime-badge">REAL-TIME</span>
                  </h2>
                  <p className="chart-card-subtitle">Cross-channel attribution analysis</p>
                </div>
                <div className="chart-tab-group">
                  {['Revenue', 'DM Interactions', 'CTR', 'CTR → Purchase'].map(tab => (
                    <button
                      key={tab}
                      className={`chart-tab-btn ${chartTab === tab ? 'active' : ''}`}
                      onClick={() => setChartTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
              <div className="chart-svg-wrapper">
                <svg width="100%" height="100%" viewBox="0 0 1000 400" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,300 L140,280 L280,250 L420,220 L560,240 L700,180 L840,150 L1000,120 L1000,400 L0,400 Z" fill="url(#revenueGradient)" />
                  <path d="M0,300 L140,280 L280,250 L420,220 L560,240 L700,180 L840,150 L1000,120" fill="none" stroke="#3B82F6" strokeWidth="2.5" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* ── AI Intent & Forecast ── */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-lg-5">
            <div className="ai-intent-container">
              <div className="ai-intent-header">
                <div className="intent-title-group">
                  <div className="intent-icon-wrapper"><BrainCircuit size={22} strokeWidth={2} /></div>
                  <span className="intent-title-text">AI Purchase Intent</span>
                </div>
                <span className="live-status-badge">LIVE</span>
              </div>
              <div className="funnel-visualization">
                <div className="funnel-pyramid">
                  <div className="funnel-dot dot-1"></div>
                  <div className="funnel-dot dot-2"></div>
                  <div className="funnel-dot dot-3"></div>
                  <div className="funnel-dot dot-4"></div>
                  <div className="funnel-dot dot-5"></div>
                </div>
              </div>
              <div className="forecast-display">
                <p className="forecast-label-text">90-DAY FORECAST</p>
                <div className="forecast-amount-wrapper">
                  <span className="forecast-amount-value">$45k</span>
                  <span className="forecast-potential-text">potential</span>
                </div>
              </div>
              <div className="intent-metrics-list">
                <div className="intent-metric-item">
                  <div className="metric-item-header">
                    <span className="metric-label">High Intent</span>
                    <span className="metric-count metric-green">4,201</span>
                  </div>
                  <div className="metric-progress-track">
                    <div className="metric-progress-bar bar-green" style={{ width: '45%' }}></div>
                  </div>
                </div>
                <div className="intent-metric-item">
                  <div className="metric-item-header">
                    <span className="metric-label">Med Intent</span>
                    <span className="metric-count metric-orange">12,840</span>
                  </div>
                  <div className="metric-progress-track">
                    <div className="metric-progress-bar bar-orange" style={{ width: '30%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-7">
            <div className="forecast-card-container">
              <div className="forecast-card-header">
                <div className="forecast-header-info">
                  <h3 className="forecast-card-title">90-DAY REVENUE FORECAST</h3>
                  <p className="forecast-card-subtitle">AI modeling of projected multi-quarter growth</p>
                </div>
                <div className="growth-badge">
                  <TrendingUp size={14} />
                  <span>PREDICTED GROWTH +64%</span>
                </div>
              </div>
              <div className="forecast-svg-wrapper">
                <svg width="100%" height="100%" viewBox="0 0 800 300" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="forecastGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,250 L266,200 L533,120 L800,50 L800,300 L0,300 Z" fill="url(#forecastGradient)" />
                  <path d="M0,250 L266,200 L533,120 L800,50" fill="none" stroke="#3B82F6" strokeWidth="2.5" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* ── Recommended Offers ── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="section-title-row">
              <h3 className="section-heading-text">RECOMMENDED OFFERS</h3>
              <button className="view-library-btn">VIEW LIBRARY</button>
            </div>
          </div>
        </div>
        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl-4">
            <div className="offer-card offer-cyan">
              <div className="offer-tag-badge">WELCOME SERIES</div>
              <h3 className="offer-card-title">10% Off</h3>
              <p className="offer-card-desc">Targeted incentive for new user activation. Increases conversion by 18% for first-time shoppers.</p>
              <div className="offer-card-footer">
                <div className="offer-metric-value">$12,400</div>
                <button className="offer-action-icon"><ArrowRight size={18} strokeWidth={2.5} /></button>
              </div>
              <div className="offer-glow-bg"></div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4">
            <div className="offer-card offer-emerald">
              <div className="offer-tag-badge">TOP PERFORMING</div>
              <h3 className="offer-card-title">Free Express Shipping</h3>
              <p className="offer-card-desc">Increases conversion by 22% for users with Cart Value &gt; $75.</p>
              <div className="offer-card-footer">
                <div className="offer-metric-value">$8,420</div>
                <button className="offer-action-icon"><ArrowRight size={18} strokeWidth={2.5} /></button>
              </div>
              <div className="offer-glow-bg"></div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4">
            <div className="offer-card offer-purple">
              <div className="offer-tag-badge">LOYALTY BUNDLE</div>
              <h3 className="offer-card-title">Buy 2 Get 1 Free</h3>
              <p className="offer-card-desc">Targeting "VIP" segment with high purchase frequency.</p>
              <div className="offer-card-footer">
                <div className="offer-metric-value">$15,200</div>
                <button className="offer-action-icon"><ArrowRight size={18} strokeWidth={2.5} /></button>
              </div>
              <div className="offer-glow-bg"></div>
            </div>
          </div>
        </div>

        {/* ── Funnel Diagnostics ── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="funnel-diagnostics-card">
              <div className="diagnostics-header">
                <div className="diagnostics-header-left">
                  <h2 className="diagnostics-title">Funnel Diagnostics</h2>
                  <p className="diagnostics-subtitle">End-to-end attribution mapping</p>
                </div>
                <div className="diagnostics-efficiency">
                  <p className="efficiency-label">OVERALL EFFICIENCY</p>
                  <p className="efficiency-value">94.8%</p>
                </div>
              </div>
              <div className="funnel-flow-container">
                <div className="flow-line"></div>
                <div className="flow-steps-wrapper">
                  <div className="flow-step-item">
                    <div className="flow-step-icon"><MousePointer2 size={24} strokeWidth={2.5} /></div>
                    <div className="flow-step-label">Post Reach</div>
                    <div className="flow-step-value">1.2M</div>
                  </div>
                  <div className="flow-step-item">
                    <div className="flow-drop-tooltip">45% Drop</div>
                    <div className="flow-step-icon"><Mail size={24} strokeWidth={2.5} /></div>
                    <div className="flow-step-label">DM Open</div>
                    <div className="flow-step-value">480K</div>
                  </div>
                  <div className="flow-step-item">
                    <div className="flow-drop-tooltip">82% Drop</div>
                    <div className="flow-step-icon"><LinkIcon size={24} strokeWidth={2.5} /></div>
                    <div className="flow-step-label">Link Click</div>
                    <div className="flow-step-value">84K</div>
                  </div>
                  <div className="flow-step-item">
                    <div className="flow-drop-tooltip">15% Drop</div>
                    <div className="flow-step-icon"><ShoppingCart size={24} strokeWidth={2.5} /></div>
                    <div className="flow-step-label">Checkout</div>
                    <div className="flow-step-value">12K</div>
                  </div>
                  <div className="flow-step-item">
                    <div className="flow-drop-tooltip">1% Drop</div>
                    <div className="flow-step-icon flow-active">
                      <CheckCircle2 size={24} strokeWidth={2.5} />
                      <span className="pulse-ring"></span>
                    </div>
                    <div className="flow-step-label">Purchase</div>
                    <div className="flow-step-value highlight">4.2K</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default Dashboard;